import React, { useState } from 'react';

export default function AIColorCleanupOverlay({
  isOpen,
  onClose,
  onConfirm, // optional: called only on backend success
  onDone,    // NEW: called after Yes flow finishes, whether success or failure
  uiDisabled = false,

  // Identity / project context
  userEmail,
  filename,
  projectRoot,

  // Existing / optional props
  numColors,
  colorList,
  inputImagePath,

  // Optional API prefix
  apiBase = '',
}) {
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const effectiveDisabled = uiDisabled || submitting;

  async function handleYes() {
    if (effectiveDisabled) return;

    let result = {
      attempted: true,
      ok: false,
      data: null,
      error: null,
    };

    // Hard guard: prevent firing backend without identity context
    if (!userEmail || !filename) {
      const err = new Error(
        '[AIColorCleanupOverlay] Missing userEmail or filename'
      );

      console.error(err, {
        userEmail,
        filename,
        projectRoot,
        numColors,
        colorList,
        inputImagePath,
      });

      result.error = err;

      try {
        await onDone?.(result);
      } catch (callbackErr) {
        console.error('[AIColorCleanupOverlay] onDone failed:', callbackErr);
      } finally {
        onClose?.();
      }
      return;
    }

    setSubmitting(true);

    try {
      const body = {
        filename,
        projectRoot: projectRoot || '',
      };

      if (typeof numColors === 'number') body.numColors = numColors;
      if (Array.isArray(colorList)) body.colorList = colorList;
      if (typeof inputImagePath === 'string' && inputImagePath.trim()) {
        body.inputImagePath = inputImagePath.trim();
      }

      const res = await fetch(`${apiBase}/ai-color-cleanup/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          user: userEmail,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`AI cleanup request failed (${res.status}): ${text}`);
      }

      const data = await res.json().catch(() => ({}));

      result = {
        attempted: true,
        ok: true,
        data,
        error: null,
      };

      if (typeof onConfirm === 'function') {
        await onConfirm(data);
      }
    } catch (err) {
      console.error(err);

      result = {
        attempted: true,
        ok: false,
        data: null,
        error: err,
      };
    } finally {
      try {
        await onDone?.(result);
      } catch (callbackErr) {
        console.error('[AIColorCleanupOverlay] onDone failed:', callbackErr);
      } finally {
        setSubmitting(false);
        onClose?.();
      }
    }
  }

  return (
    <div
      onClick={effectiveDisabled ? undefined : onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(15,23,42,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2vh',
        boxSizing: 'border-box',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(92vw, 640px)',
          borderRadius: '14px',
          border: '1px solid rgba(148,163,184,0.6)',
          background: 'rgba(255,255,255,0.95)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            padding: '18px',
            borderBottom: '1px solid rgba(148,163,184,0.35)',
            fontSize: '18px',
            fontWeight: 800,
            color: '#0f172a',
          }}
        >
          AI Color Cleanup
        </div>

        <div
          style={{
            padding: '22px 18px',
            color: '#0f172a',
            fontSize: '16px',
            lineHeight: 1.5,
          }}
        >
          <div style={{ marginBottom: 14 }}>
            You are about to use the AI Color Cleanup feature.
            <br />
            WARNING: Some AI hallucinations may occur.
          </div>

          <div style={{ marginBottom: 14 }}>
            Please note that the number of uses of this feature is limited.
          </div>

          <div style={{ fontWeight: 700 }}>Shall we continue?</div>
        </div>

        <div
          style={{
            padding: '16px 18px 22px',
            display: 'flex',
            justifyContent: 'center',
            gap: 16,
          }}
        >
          <button
            type="button"
            onClick={effectiveDisabled ? undefined : onClose}
            disabled={effectiveDisabled}
            style={{
              minWidth: 110,
              padding: '10px 18px',
              borderRadius: 10,
              border: '1px solid rgba(148,163,184,0.7)',
              background: 'white',
              cursor: effectiveDisabled ? 'not-allowed' : 'pointer',
              opacity: effectiveDisabled ? 0.6 : 1,
              fontWeight: 700,
            }}
          >
            No
          </button>

          <button
            type="button"
            onClick={effectiveDisabled ? undefined : handleYes}
            disabled={effectiveDisabled}
            style={{
              minWidth: 110,
              padding: '10px 18px',
              borderRadius: 10,
              border: '1px solid rgba(2,132,199,0.75)',
              background: '#0ea5e9',
              color: 'white',
              cursor: effectiveDisabled ? 'not-allowed' : 'pointer',
              opacity: effectiveDisabled ? 0.6 : 1,
              fontWeight: 800,
            }}
          >
            {submitting ? 'Working...' : 'Yes'}
          </button>
        </div>
      </div>
    </div>
  );
}